import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { CLI_AUTH } from '@test-kit/selectors/cli-auth';
import { useAuth } from '@frontend/contexts/AuthContext';
import {
  buildCliCallbackUrl,
  readCliAuthParams,
  requestCliAuthorization,
} from './cliAuthPageUtils';

const BUILDER_API_URL = import.meta.env.VITE_BUILDER_API_URL || '';

function CliAuthPage() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const authParams = useMemo(() => readCliAuthParams(location.search), [location.search]);

  useEffect(() => {
    if (authParams.ok && !currentUser) {
      navigate(`/cli-auth-login${location.search}`, { replace: true });
    }
  }, [authParams.ok, currentUser, location.search, navigate]);

  const handleAuthorize = async () => {
    if (!authParams.ok || !currentUser || status === 'authorizing') {
      return;
    }

    setStatus('authorizing');
    setError('');

    try {
      const authorization = await requestCliAuthorization({
        currentUser,
        authParams,
        apiBaseUrl: BUILDER_API_URL,
      });
      setStatus('redirecting');
      window.location.assign(buildCliCallbackUrl(authParams.redirectUri, authorization.code, authParams.state));
    } catch (err) {
      setStatus('idle');
      setError(err instanceof Error ? err.message : 'CLI authorization failed.');
    }
  };

  if (!authParams.ok) {
    return (
      <CliAuthShell>
        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          CLI authorization failed
        </Typography>
        <Typography color="error" data-testid={CLI_AUTH.error}>
          {authParams.error}
        </Typography>
      </CliAuthShell>
    );
  }

  if (!currentUser) {
    return (
      <CliAuthShell>
        <CircularProgress size={24} sx={{ mb: 2 }} />
        <Typography color="text.secondary" data-testid={CLI_AUTH.status}>
          Redirecting to sign in...
        </Typography>
      </CliAuthShell>
    );
  }

  return (
    <CliAuthShell>
      <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
        Authorize Segmently CLI
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }} data-testid={CLI_AUTH.status}>
        {status === 'redirecting'
          ? 'Returning to the CLI...'
          : 'Approve this request to finish login in your terminal.'}
      </Typography>
      {error && (
        <Typography color="error" sx={{ mb: 2 }} data-testid={CLI_AUTH.error}>
          {error}
        </Typography>
      )}
      <Button
        type="button"
        variant="contained"
        onClick={handleAuthorize}
        disabled={status !== 'idle'}
        data-testid={CLI_AUTH.authorizeButton}
      >
        {status === 'authorizing' ? <CircularProgress size={20} color="inherit" /> : 'Authorize CLI'}
      </Button>
    </CliAuthShell>
  );
}

function CliAuthShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: '100svh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
      }}
      data-testid={CLI_AUTH.page}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            textAlign: 'center',
            bgcolor: 'background.paper',
          }}
        >
          {children}
        </Paper>
      </Container>
    </Box>
  );
}

export default CliAuthPage;
